import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { getTrasladoEstadoColor } from "../../../../../constants/trasladosEstados";
import { autorizarTraslado, fetchAgencias } from "../../../../../actions/traslados";
import snackbarUtils from "../../../../../utils/snackbarUtils";
import { StyledTrasladosDialog } from "../../../../styled/traslados";

const formatUbicacion = (calle, numero, piso, depto, localidad, provincia) => {
  const direccion = [
    calle,
    numero,
    piso ? `Piso ${piso}` : null,
    depto ? `Depto ${depto}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const zona = [localidad, provincia].filter(Boolean).join(" - ");

  if (direccion && zona) return `${direccion} (${zona})`;
  return direccion || zona || "—";
};

const SectionHeader = ({ title }) => (
  <StyledTrasladosDialog.SectionHeader>
    <StyledTrasladosDialog.SectionTitle variant="h6">
      {title}
    </StyledTrasladosDialog.SectionTitle>
    <StyledTrasladosDialog.SectionDivider />
  </StyledTrasladosDialog.SectionHeader>
);

const VisualizarTramoDialog = ({
  open,
  onClose,
  data,
  isAssigning = false,
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedAgencia, setSelectedAgencia] = useState("");

  const agenciasState = useSelector((state) => state.traslados.agencias);
  const agencias = agenciasState?.data || [];

  useEffect(() => {
    if (open && isAssigning) {
      dispatch(fetchAgencias("activas", ""));
    }
  }, [open, isAssigning, dispatch]);

  useEffect(() => {
    if (data?.idAgencia) {
      setSelectedAgencia(String(data.idAgencia));
    } else {
      setSelectedAgencia("");
    }
  }, [data]);

  if (!data) return null;

  const nroTramo = data.nroTramoUnico ?? data.nroOrden ?? "—";
  const nroTraslado = data.nroTraslado || data.idTraslado || "—";
  const estadoColor =
    data.estadoTramoColor || getTrasladoEstadoColor(data.estadoTramoNombre);
  const isReasignacion = data.estadoTramoNombre === "Rechazado por Agencia";

  const handleAssignSubmit = () => {
    if (!selectedAgencia) {
      snackbarUtils.error("Debe seleccionar una agencia.");
      return;
    }
    setLoading(true);
    dispatch(autorizarTraslado(data.idTramo, parseInt(selectedAgencia, 10), false))
      .then(() => {
        snackbarUtils.success(
          isReasignacion
            ? "Agencia reasignada y tramo despachado."
            : "Agencia asignada correctamente."
        );
        onRefresh?.();
        onClose();
      })
      .catch(() => {
        snackbarUtils.error("Ocurrió un error al asignar la agencia.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const headerTitle = isAssigning
    ? `${isReasignacion ? "REASIGNAR" : "ASIGNAR"} AGENCIA — TRAMO ${nroTramo}`
    : `DETALLE TRAMO ${nroTramo} — TRASLADO ${nroTraslado}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledTrasladosDialog.Header>
        {headerTitle}
        <StyledTrasladosDialog.CloseButton
          aria-label="close"
          onClick={onClose}
        >
          <CloseIcon />
        </StyledTrasladosDialog.CloseButton>
      </StyledTrasladosDialog.Header>

      <StyledTrasladosDialog.Content>
        {/* Chips de estado y sentido */}
        <StyledTrasladosDialog.ChipsContainer>
          <StyledTrasladosDialog.StatusChip
            label={data.estadoTramoNombre || "Sin estado"}
            size="small"
            chipColor={estadoColor || "#9e9e9e"}
          />
          <StyledTrasladosDialog.SentidoChip
            label={data.esRetorno ? "Vuelta" : "Ida"}
            size="small"
            variant="outlined"
          />
          {data.posibleNegativo && (
            <StyledTrasladosDialog.PosibleNegativoChip
              label="Posible Negativo"
              size="small"
            />
          )}
        </StyledTrasladosDialog.ChipsContainer>

        {/* Datos Generales */}
        <SectionHeader title="Datos generales" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Nro. Traslado
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {nroTraslado}
            </StyledTrasladosDialog.FieldValue>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Siniestro
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {data.siniestro || data.idSiniestro || "—"}
            </StyledTrasladosDialog.FieldValue>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Trabajador
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {data.trabajador ||
                [data.nombreTrabajador, data.apellidoTrabajador].filter(Boolean).join(" ") ||
                "—"}{" "}
              {data.documentoTrabajador || data.dni ? `· DNI ${data.documentoTrabajador || data.dni}` : ""}
            </StyledTrasladosDialog.FieldValue>
          </Grid>
        </Grid>

        {/* Recorrido */}
        <SectionHeader title="Recorrido del tramo" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Origen
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {formatUbicacion(
                data.calleOrigen,
                data.numeroOrigen,
                data.pisoOrigen,
                data.deptoOrigen,
                data.localidadOrigen,
                data.provinciaOrigen
              )}
            </StyledTrasladosDialog.FieldValue>
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Destino
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {formatUbicacion(
                data.calleDestino,
                data.numeroDestino,
                data.pisoDestino,
                data.deptoDestino,
                data.localidadDestino,
                data.provinciaDestino
              )}
            </StyledTrasladosDialog.FieldValue>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Fecha y hora
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {data.fechaHoraTraslado
                ? moment(data.fechaHoraTraslado).format("DD/MM/YYYY HH:mm")
                : "—"}
            </StyledTrasladosDialog.FieldValue>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Distancia
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {data.distancia !== null && data.distancia !== undefined
                ? `${Number(data.distancia).toLocaleString("es-AR", { maximumFractionDigits: 1 })} km`
                : "—"}
            </StyledTrasladosDialog.FieldValue>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Tipo de Transporte
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {data.tipoTransporteNombre || data.transporte || "—"}
            </StyledTrasladosDialog.FieldValue>
          </Grid>
        </Grid>

        {/* Gestión Operativa */}
        <SectionHeader title="Gestión operativa" />
        <Grid container spacing={2}>
          {isAssigning ? (
            <Grid item xs={12} sm={6}>
              <StyledTrasladosDialog.FieldLabel variant="body2" sx={{ mb: 0.5 }}>
                Agencia *
              </StyledTrasladosDialog.FieldLabel>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedAgencia}
                  onChange={(e) => setSelectedAgencia(e.target.value)}
                  disabled={loading}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Sin asignar</em>
                  </MenuItem>
                  {agencias.map((a) => (
                    <MenuItem key={a.idAgencia} value={String(a.idAgencia)}>
                      {a.razonSocial}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <Grid item xs={12} sm={4}>
              <StyledTrasladosDialog.FieldLabel variant="body2">
                Agencia asignada
              </StyledTrasladosDialog.FieldLabel>
              <StyledTrasladosDialog.FieldValue variant="body2">
                {data.agenciaNombre || "—"}
              </StyledTrasladosDialog.FieldValue>
            </Grid>
          )}

          <Grid item xs={12} sm={isAssigning ? 3 : 4}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Gestor
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {data.gestor || "—"}
            </StyledTrasladosDialog.FieldValue>
          </Grid>
          <Grid item xs={12} sm={isAssigning ? 3 : 4}>
            <StyledTrasladosDialog.FieldLabel variant="body2">
              Prestador
            </StyledTrasladosDialog.FieldLabel>
            <StyledTrasladosDialog.FieldValue variant="body2">
              {data.prestadorNombre || "—"}
            </StyledTrasladosDialog.FieldValue>
          </Grid>

          {Boolean(data.observTramo || data.observaciones) && (
            <Grid item xs={12}>
              <StyledTrasladosDialog.FieldLabel variant="body2" sx={{ mb: 0.5 }}>
                Observaciones del tramo
              </StyledTrasladosDialog.FieldLabel>
              <StyledTrasladosDialog.ObservationBox variant="body2">
                {data.observTramo || data.observaciones}
              </StyledTrasladosDialog.ObservationBox>
            </Grid>
          )}
        </Grid>
      </StyledTrasladosDialog.Content>

      <StyledTrasladosDialog.Actions>
        {isAssigning ? (
          <>
            <Button onClick={onClose} color="inherit" disabled={loading}>
              CANCELAR
            </Button>
            <StyledTrasladosDialog.PrimaryButton
              onClick={handleAssignSubmit}
              variant="contained"
              disabled={loading || !selectedAgencia}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : isReasignacion ? (
                "REASIGNAR Y DESPACHAR"
              ) : (
                "GUARDAR ASIGNACIÓN"
              )}
            </StyledTrasladosDialog.PrimaryButton>
          </>
        ) : (
          <StyledTrasladosDialog.PrimaryButton
            onClick={onClose}
            variant="contained"
          >
            CERRAR
          </StyledTrasladosDialog.PrimaryButton>
        )}
      </StyledTrasladosDialog.Actions>
    </Dialog>
  );
};

export default VisualizarTramoDialog;
